import * as React from 'react';
import {
  SortButton,
  SortOrderType,
  TableSortBreadcrumbProps,
} from '@interfaces/search.interfaces';
import TableSortHeader, {
  TableSortHeaderCta,
} from '@components/TableComponents/TableSortHeader';
import { ResponsivePagination } from '@components/TableComponents/CustomTableComponents';
import { SearchBarProps } from '@components/SearchBar';
import { TableSortProvider } from '@common/context/tableSortContext';
import { Box, useTheme } from '@mui/material';
import EcCrownWithLines from '@components/EcCrownWithLines';
import DashButton, { DashButtonProps } from '@components/DashButton';
import { styled } from '@mui/system';

const Styled = {
  FlexRow: styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    rowGap: '1.5em',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
  })),
  CrownBox: styled(Box)({
    flex: 2,
    display: 'flex',
    justifyContent: 'center',
  }),
};

export interface TableSortWrapperProps {
  sortButtons?: SortButton[];
  initialRowsPerPage?: number;
  initialSortCategory: string;
  initialSortOrder?: SortOrderType;
  rowsPerPageOptions?: number[];
  totalCount?: number;
  displayMobile: boolean;
  disabled?: boolean;
  // Key for session storage where table selections are saved
  sessionKey?: string;
  searchBarProps?: SearchBarProps;
  centerPagination?: boolean;
  errorText?: string;
  breadcrumb?: TableSortBreadcrumbProps;
  styledHeader?: boolean;
  cta?: TableSortHeaderCta;
  ctaButton?: DashButtonProps;
  companyEnabled?: boolean;
  showCrown?: boolean;
}

const TableWrapperWithSort: React.FC<TableSortWrapperProps> = ({
  children,
  sortButtons,
  initialRowsPerPage = 10,
  initialSortCategory,
  initialSortOrder = 'DESC',
  rowsPerPageOptions,
  totalCount,
  displayMobile,
  disabled,
  sessionKey,
  searchBarProps,
  centerPagination = false,
  errorText,
  breadcrumb,
  styledHeader = false,
  cta,
  ctaButton,
  companyEnabled,
  showCrown,
}) => {
  const theme = useTheme();
  const [tableSelections, setTableSelections] = React.useState({
    sortBy: initialSortCategory,
    sortOrder: initialSortOrder,
    page: 0,
    rowsPerPage: initialRowsPerPage,
  });

  const [sortMenuAnchor, setSortMenuAnchor] =
    React.useState<HTMLElement | null>(null);

  const sortMenuOpen = Boolean(sortMenuAnchor);

  const _breadcrumb = breadcrumb
    ? { ...breadcrumb, prevLocation: undefined } // Removed headerTitle
    : undefined;

  React.useEffect(() => {
    if (sessionKey) {
      const tableSelections = sessionStorage.getItem(sessionKey);

      if (tableSelections) {
        const parsedSelections = JSON.parse(tableSelections);
        setTableSelections(parsedSelections);
      }
    }
  }, []);

  React.useEffect(() => {
    if (sessionKey) {
      sessionStorage.setItem(sessionKey, JSON.stringify(tableSelections));
    }
  }, [tableSelections]);

  const handleSortMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setSortMenuAnchor(sortMenuAnchor ? null : event.currentTarget);
  };

  const handleSortClick = <T,>(
    event: React.MouseEvent<unknown>,
    category: keyof T
  ): void => {
    const isAsc = sortBy === category && sortOrder === 'ASC';
    setTableSelections({
      ...tableSelections,
      sortOrder: isAsc ? 'DESC' : 'ASC',
      sortBy: category as string,
      page: 0,
    });

    setTimeout(() => {
      setSortMenuAnchor(null);
    }, 100);
  };

  const handleClickAway = (): void => {
    sortMenuAnchor && setSortMenuAnchor(null);
  };

  const handlePageChange = (event, newPage: number): void => {
    setTableSelections({
      ...tableSelections,
      page: newPage,
    });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setTableSelections({
      ...tableSelections,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const { sortBy, sortOrder, page, rowsPerPage } = tableSelections;

  return (
    <TableSortProvider
      value={{
        sortBy,
        sortOrder,
        sortMenuOpen,
        sortMenuAnchor,
        handleSortMenuOpen,
        handleClickAway,
        handleSortClick,
        page,
        rowsPerPage,
      }}
    >
      <TableSortHeader
        buttons={sortButtons}
        disabled={!!disabled}
        searchBarProps={searchBarProps}
        title={undefined} // Removed headerTitle
        errorText={errorText}
        breadcrumb={_breadcrumb}
        styledHeader={styledHeader}
        cta={cta}
      />
      <Styled.FlexRow>
        {ctaButton && companyEnabled === true && (
          <DashButton
            href={ctaButton.href}
            variant={ctaButton.variant}
            icon={ctaButton.icon}
            dataTestId={ctaButton.dataTestId}
            label={ctaButton.label}
            sx={{
              ...ctaButton.sx,
              minWidth: 0,
              maxWidth: '169px',
              [theme.breakpoints.down('sm')]: {
                maxWidth: 'unset',
              },
            }}
          />
        )}
        {showCrown && ( // Control crown visibility with showCrown prop
          <Styled.CrownBox>
            <EcCrownWithLines
              opacity={0.5}
              maxWidth="60%"
              wrapperStyle={{
                transform: 'scale(0.7)',
                [theme.breakpoints.down('sm')]: {
                  display: 'none',
                },
              }}
            />
          </Styled.CrownBox>
        )}
      </Styled.FlexRow>
      {children}
      {!!totalCount && (
        <ResponsivePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          handleRowsPerPageChange={handleRowsPerPageChange}
          displayMobile={displayMobile}
          centerPagination={centerPagination}
        />
      )}
    </TableSortProvider>
  );
};

export default TableWrapperWithSort;
